import { useRef } from "react"
import { toast } from "sonner"

import { InlineInput } from "./inline-input"
import { InlineInputProps } from "./props"

const DEFAULT_ERROR_MESSAGE = "Please enter a valid URL starting with http"
const DEFAULT_ALLOWED_PROTOCOLS = ["https:", "http:"]

function isValidUrl(value: string, allowedProtocols: string[]) {
  const trimmed = value.trim()
  if (trimmed === "") return true

  try {
    const url = new URL(trimmed)
    return allowedProtocols.includes(url.protocol)
  } catch {
    return false
  }
}

export interface InlineUrlInputProps
  extends Omit<InlineInputProps<string | null>, "onSave" | "validator" | "parser" | "formatter"> {
  onSave?: (value: string | null) => void | Promise<void>
  errorMessage?: string
  allowedProtocols?: string[]
}

export function InlineUrlInput({
  value,
  defaultValue,
  onSave,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  allowedProtocols = DEFAULT_ALLOWED_PROTOCOLS,
  ...rest
}: InlineUrlInputProps) {
  const lastInvalidValueRef = useRef<string | null>(null)

  const validator = (input: string) => isValidUrl(input, allowedProtocols)

  const handleSave = async (rawValue: string) => {
    if (!validator(rawValue)) {
      if (lastInvalidValueRef.current !== rawValue) {
        toast.error(errorMessage)
        lastInvalidValueRef.current = rawValue
      }
      throw new Error("Invalid URL")
    }

    lastInvalidValueRef.current = null

    if (onSave) {
      const trimmed = rawValue.trim()
      await onSave(trimmed === "" ? null : trimmed)
    }
  }

  return (
    <InlineInput
      {...rest}
      value={value ?? undefined}
      defaultValue={defaultValue ?? ""}
      validator={validator}
      resetOnInvalid={false}
      onSave={handleSave}
    />
  )
}
